// The HOW IT WORKS fold of a game menu (mockup round 091 card 07, R1895;
// ui-map-interface.md §12 "The HOW IT WORKS fold").
//
// Each of the six game menus ends with ONE fold, shared by its pills: a
// 36 px row, closed by default, with the fact count at the right. Open, it
// lists each fact of the menu once, as a key and a short value in the fact
// rows of the card kit (`factsHtml`). A fact that a row or a card already
// states is not here: a risk stands on its row. The rules of training and
// practice, the per-point rate and the practice measure stand here and not
// on the captain card, because they are the same for every captain (R2087). The row fold under ⋯ of an object
// row (R1892) is another thing.
//
// The fold remembers its state per menu and per viewer in `localStorage`
// (`ui/localstore.ts`), a per-browser convenience: a store that refuses
// gives a closed fold.
//
// An empty line that named a route in words is now one short line and a
// GO TO press, which opens the pill where the thing is made through the
// pill navigation of the overlay (`goto`).
//
// Every figure comes from where the sentence it replaced took it: the
// catalog rules, the compile-time pack, or the wire record of the survey.
// The module is pure apart from the store.

import { SHARED_INTEL_LEVEL } from '../../gen/balance';
import { PRODUCTION_SMOOTH_SAMPLES } from '../../gen/presentation';
import { overlay as S } from '../../gen/strings';
import type { GameState } from '../../state';
import { arrowFace } from '../arrowpress';
import { buttonHtml, type Fact, factsHtml } from '../cards/card';
import { esc } from '../esc';
import { readStore, writeStore } from '../localstore';
import type { CaptainRules } from '../../gen/proto/aquaphobia';
import { pctNum, perPointText, practiceText } from './captaincard';
import { attrName } from './caprow';
import { ATTR_SHORT, GAME_TABS, type Tab, tabLabel } from './tables';

/** A menu that has a fold: one of the six game menus. */
type HowTab = typeof GAME_TABS[number];

/** Is `t` a game menu, which ends with a fold? */
export function hasFold(t: Tab): t is HowTab {
  return (GAME_TABS as readonly string[]).includes(t);
}

/** The store key of one menu's fold. */
export const howKey = (t: HowTab): string => `ap.howfold.${t.toLowerCase()}`;

/** The state each fold was read at, so the 4 Hz render reads the store
 *  once per menu. */
const known = new Map<HowTab, boolean>();

/** Is the fold of `t` open for this viewer? Closed by default. */
export function foldOpen(t: HowTab): boolean {
  let v = known.get(t);
  if (v === undefined) {
    v = readStore(howKey(t)) === '1';
    known.set(t, v);
  }
  return v;
}

/** Opens or closes the fold of `t`, and remembers it. */
export function setFoldOpen(t: HowTab, open: boolean): void {
  known.set(t, open);
  writeStore(howKey(t), open ? '1' : '0');
}

/** Forgets what the store said, for a test. */
export function forgetFolds(): void {
  known.clear();
}

const fact = (key: string, val: string): Fact => ({ key, val: esc(val) });

/** The survey cadence in whole game-minutes, from the record of the
 *  survey the wire sent (the WAR record or the income record), or 0
 *  while neither has come. */
function surveyMin(s: GameState): number {
  const sec = s.war?.sampleEverySec || s.production?.sampleEverySec || 0;
  return Math.round(sec / 60);
}

/** The Survey fact, or nothing while no survey record has come. */
function survey(s: GameState): Fact[] {
  const min = surveyMin(s);
  return min > 0 ? [fact(S.how_k_survey, S.how_survey({ min }))] : [];
}

/**
 * The rules of training and practice that left the captain card (round
 * 100 card 01, R2087): each is the same for every captain, so the card
 * states only what follows from them for one captain. The talent factor
 * speeds the Academy and practice alike (numbers.md §12).
 */
function trainingFacts(s: GameState, c: CaptainRules): Fact[] {
  const mul = s.rules.talentTrainingMul;
  return [
    fact(S.how_k_training, S.how_training({ hours: pctNum(c.trainingHoursPerPoint) })),
    ...(mul > 0 ? [fact(S.how_k_talent, S.how_talent({ mul: pctNum(mul) }))] : []),
    fact(S.how_k_cap, S.how_cap({ cap: c.attrTrainCap })),
    fact(S.how_k_practice, S.how_practice),
    fact(S.how_k_season_cap, S.how_season_cap({
      cap: c.practiceSeasonCap, growth: pctNum(c.practicePointGrowth),
    })),
    fact(S.how_k_in_decline, S.how_in_decline({ pct: pctNum(c.practiceDeclinePct) })),
  ];
}

/** One row for each attribute (R2087): what one point is worth, then the
 *  measure that grows it at sea. The row names the attribute from the
 *  catalog, as the captain card does. */
function pointFacts(s: GameState, c: CaptainRules | undefined): Fact[] {
  if (!c) return [];
  return ATTR_SHORT.map((short, i) => fact(attrName(s, i) || short, S.how_attr({
    rate: perPointText(i, c), measure: practiceText(i),
  })));
}

/** The facts of one menu, each once, in reading order. */
export function howFacts(s: GameState, t: HowTab): Fact[] {
  const r = s.rules;
  switch (t) {
    case 'BUILD':
      return [
        fact(S.how_k_shipyard, S.how_shipyard),
        fact(S.how_k_keystone, S.how_keystone),
      ];
    case 'DRAFT':
      return [
        fact(S.how_k_path, S.how_draft_path),
        fact(S.how_k_roll, S.how_roll({ low: r.inventionRollMin, high: r.inventionRollMax })),
      ];
    case 'FLEET':
      return [
        fact(S.how_k_path, S.how_fleet_path),
        fact(S.how_k_slots, S.how_fleet_slots),
      ];
    case 'CAPTAIN': {
      const c = r.captain;
      const career = c ? [
        fact(S.how_k_age, S.how_age({ min: c.recruitAgeMin, max: c.recruitAgeMax, years: c.yearsPerSeason })),
        fact(S.how_k_career, S.how_career({ peak: c.peakAtAge, decline: c.declineAtAge })),
        fact(S.how_k_peak, S.how_peak({ factor: pctNum(c.peakTrainingFactor) })),
        fact(S.how_k_decline, S.how_decline),
        fact(S.how_k_retire, S.how_retire({ base: pctNum(c.retireBasePct), per: pctNum(c.retirePerSeasonPct) })),
        ...trainingFacts(s, c),
      ] : [];
      return [...career, fact(S.how_k_brig, S.how_brig), fact(S.how_k_capture, S.how_capture), ...pointFacts(s, c)];
    }
    case 'ECONOMY':
      return [
        ...survey(s),
        fact(S.how_k_smoothing, S.how_smoothing({ n: PRODUCTION_SMOOTH_SAMPLES })),
        fact(S.how_k_sells, S.how_sells),
        fact(S.how_k_slots, S.how_auction_slots({
          lots: r.isotopeLotsPerRadControlLevel, prints: r.printListingsPerShipDesignLevel,
        })),
        fact(S.how_k_escrow, S.how_escrow),
        fact(S.how_k_lot, S.how_lot({ min: r.isotopeLotMin, max: r.isotopeLotMax })),
        fact(S.how_k_print_sale, S.how_print_sale),
      ];
    case 'WAR':
      return [
        ...survey(s),
        fact(S.how_k_shelf, S.how_shelf({ hours: Math.round(s.mapToWorldSec(r.intelTtlSec) / 3600) })),
        fact(S.how_k_squad, S.how_squad({ level: SHARED_INTEL_LEVEL })),
      ];
  }
}

/**
 * The fold as markup: the row (the arrow, HOW IT WORKS, the count) and,
 * while open, the fact rows. A menu with no fact draws no fold. `key` is
 * the `data-key` the press carries: a game menu, or another host's own
 * word (the arcade build pane, R1933).
 */
export function howFoldHtml(key: string, facts: readonly Fact[], open: boolean): string {
  if (facts.length === 0) return '';
  return `<div class="hw${open ? ' open' : ''}">`
    + `<button class="hw-row" data-act="howfold" data-key="${key}" aria-expanded="${open}">`
    + `${arrowFace(open ? 'down' : 'right')}<b>${S.how_head}</b>`
    + `<i class="hw-n">${S.how_count({ n: facts.length })}</i></button>`
    + (open ? factsHtml(facts, 'hw-facts') : '')
    + '</div>';
}

/** The fold of the menu `t` for this viewer. */
export function howFold(s: GameState, t: Tab): string {
  return hasFold(t) ? howFoldHtml(t, howFacts(s, t), foldOpen(t)) : '';
}

/** The row a GO TO press names in its pill (R3263): the kind and the id of
 *  the info button of the row, as a build row writes them. */
export interface GoRow {
  kind: string;
  id: number;
}

/**
 * An empty line with its GO TO press (R1895): the state in one short
 * line, and a quiet press that opens `tab` · `sub`. `text` is escaped
 * markup. A `row` names the row the press scrolls to and flashes in the
 * pill (R3263): the building that the empty line lacks.
 */
export function emptyGoHtml(text: string, tab: Tab, sub: string, row?: GoRow): string {
  return `<div class="empty go"><span>${text}</span>`
    + buttonHtml({
      act: 'goto', dataKey: `${tab}:${sub}${row ? `:${row.kind}:${row.id}` : ''}`, face: 'quiet',
      label: S.how_go({ where: tabLabel(sub) }),
    }, { keycaps: false })
    + '</div>';
}

/** The menu and the pill a GO TO press names, and the selector of the info
 *  button of its row when it names one, or null. */
export function goTarget(key: string | undefined): { tab: Tab; sub: string; selector?: string } | null {
  const [tab, sub, kind, id] = (key ?? '').split(':');
  if (!tab || !sub || !hasFold(tab as Tab)) return null;
  if (!kind || !/^[a-z]+$/.test(kind) || !/^\d+$/.test(id ?? '')) return { tab: tab as Tab, sub };
  return { tab: tab as Tab, sub, selector: `[data-act="info"][data-kind="${kind}"][data-id="${id}"]` };
}
